import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  ChangePasswordInputSchema,
  initials,
  PASSWORD_MIN_LENGTH,
  type ChangePasswordInput,
} from '@repo/shared';
import { FormTextField, InitialsAvatar, Spinner, useToast } from '@repo/ui';
import { api, isApiError } from '../lib/api';
import { PasswordAdornment } from '../pages/auth/LoginPage';
import { useMe } from './session';

/**
 * « Mon compte » (not in the design: dialog + form patterns): who is signed in, with which roles, and the
 * password change. Every other session of the account is closed by the API; this one stays open.
 */
export function AccountDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const me = useMe();
  const [show, setShow] = useState(false);
  const form = useForm<ChangePasswordInput>({
    resolver: zodResolver(ChangePasswordInputSchema),
    defaultValues: { current: '', password: '', confirm: '' },
  });
  const change = useMutation({
    mutationFn: (v: ChangePasswordInput) => api('/auth/change-password', { method: 'POST', body: v }),
    onSuccess: () => {
      toast(t('account.passwordChanged'), { tone: 'ok' });
      onClose();
    },
    onError: (e) => {
      if (isApiError(e, 'BAD_REQUEST'))
        form.setError('current', { message: 'account.errors.wrongPassword' }, { shouldFocus: true });
    },
  });
  const err = (m?: string) => (m ? t(m, { min: PASSWORD_MIN_LENGTH }) : undefined);
  const adornment = <PasswordAdornment show={show} onToggle={() => setShow((s) => !s)} size={40} />;
  const failed = change.isError && !isApiError(change.error, 'BAD_REQUEST');
  const name = me.displayName;

  return (
    <Dialog open onClose={onClose} aria-labelledby="account-title" fullWidth maxWidth="xs">
      <Box
        component="form"
        noValidate
        onSubmit={form.handleSubmit((v) => change.mutate(v))}
        sx={{ display: 'contents' }}
      >
        <DialogTitle id="account-title">{t('account.title')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: '8px !important' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <InitialsAvatar initials={initials(`${me.firstName} ${me.lastName}`)} size={40} />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" dir="auto">
                {name}
              </Typography>
              <Typography
                variant="body2"
                dir="ltr"
                sx={{ color: 'text.secondary', unicodeBidi: 'isolate', overflowWrap: 'anywhere' }}
              >
                {me.email ?? me.matricule}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.tertiary' }}>
                {me.roles.map((r) => t(`roles.${r}`)).join(' · ')}
              </Typography>
            </Box>
          </Box>
          <Box>
            <Typography variant="subtitle2" component="h3">
              {t('account.passwordTitle')}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('account.passwordHint', { min: PASSWORD_MIN_LENGTH })}
            </Typography>
          </Box>
          <FormTextField
            label={t('account.current')}
            type={show ? 'text' : 'password'}
            autoComplete="current-password"
            error={err(form.formState.errors.current?.message)}
            endAdornment={adornment}
            {...form.register('current')}
          />
          <FormTextField
            label={t('account.new')}
            type={show ? 'text' : 'password'}
            autoComplete="new-password"
            error={err(form.formState.errors.password?.message)}
            {...form.register('password')}
          />
          <FormTextField
            label={t('account.confirm')}
            type={show ? 'text' : 'password'}
            autoComplete="new-password"
            error={err(form.formState.errors.confirm?.message)}
            {...form.register('confirm')}
          />
          {failed && (
            <Typography role="alert" variant="body2" sx={{ color: 'error.main' }}>
              {t('account.errors.generic')}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            disabled={change.isPending}
            startIcon={change.isPending ? <Spinner /> : undefined}
          >
            {t('account.submit')}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
